import { Section, WizardNew as Wizard } from "../../../../components/wizard";
import { useWizardStepsManager } from "hooks/useWizardStepsManager";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useResolvedPath } from "react-router";
import { Dimmer, Loader } from "semantic-ui-react";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { connect, type ConnectedProps } from "react-redux";
import { reset } from "../state/GroupTemplateSlice";
import CreateConfiguration from "./CreateConfiguration";
import CreateCustomers from "./CreateCustomers";
import CreateEnrollment from "./CreateEnrollment";
import CreatePacks from "./CreatePacks";
import Observable from "utils/Observable";
import { GroupTemplatePacksConfirmationModal } from "components/assignmentModals/groupTemplatePacksConfirmationModal/GroupTemplatePacksConfirmationModal";
import { savePacks } from "../state/thunks/TemplateAvailablePacksThunk";
import { type PackMap } from "components/assignmentModals/groupTemplatePacksConfirmationModal/types";
import { bindAction } from "interfaces";
import { AlertTypes, TemplateTypes } from "enums";
import { NotifyStepSwitch } from "components/notifyStep/NotifyStepSwitch";
import NotifyWizardStep from "components/notifyStep/NotifyWizardStep/NotifyWizardStep";
import { createNotificationSettings } from "features/People/Groups/AddGroup/state/addGroupActions";
import useNotify from "../hooks/useNotify";
import { sendTransientNotification } from "features/Notifications/state/notificationsActions";
import { addDomainToAccount } from "../../../Accounts/state/thunks/accountDomainsThunk";
import { reset as resetCustomers } from "../state/TemplateAvailableCustomersSlice";
import { reset as resetAvailableDomains } from "../../../Accounts/state/slices/accountDomainsSlice";

export type Props = PropsFromRedux;

enum Pages {
  Configuration,
  Enrollment,
  Customers,
  Packs,
  Notify,
}

const pageIndexes = [Pages.Configuration, Pages.Enrollment, Pages.Customers, Pages.Packs, Pages.Notify];

export function CreateGroupTemplate(props: Props) {
  const {
    isLoadingGroupInfo,
    groupId,
    isEnrollmentValid,
    resetTemplateState,
    selectedCustomerIds,
    selectedPackIds,
    addPacksToGroupTemplate,
    addNotificationSettings,
    sendNotification,
    addDomainToAccountAction,
    resetCustomers,
    resetAvailableDomainsAction,
  } = props;
  const rootPath = useResolvedPath("..").pathname;
  const [isConfigValid, setIsConfigValid] = useState(false);
  const [isConfigDirty, setIsConfigDirty] = useState(false);
  const [wizardPages, { onNext, onPrevious, onFinish }] = useWizardStepsManager(pageIndexes);
  const selectedPacks = useRef<PackMap>(new Map());
  const onTriggerPacksConfirmationObserver = useMemo(() => new Observable(), []);
  const [notyStep] = useNotify(addNotificationSettings);

  useEffect(() => {
    return () => {
      resetTemplateState();
      resetCustomers();
      resetAvailableDomainsAction();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    notyStep.notifyConfig.shouldNotifyUsers = selectedCustomerIds.length > 0;
  }, [selectedCustomerIds, notyStep]);

  const navigate = useNavigate();

  const onCancel = () => navigate(rootPath);

  const handleFinish = async () => {
    await notyStep.saveNotificationSettings(`TemplateGroup_${groupId}`);
    sendNotification({
      type: AlertTypes.success,
      message: "Template has been created!",
    });

    onFinish();
    navigate(rootPath);
  };

  const shouldShowConfirmationModal = useMemo(() => {
    return selectedCustomerIds.length > 0 && selectedPackIds.length > 0;
  }, [selectedCustomerIds, selectedPackIds]);

  const handleOnFinish = async () => {
    if (shouldShowConfirmationModal) {
      onTriggerPacksConfirmationObserver.notify(() => {
        addPacksToGroupTemplate(groupId, [...selectedPacks.current.values()], selectedCustomerIds);
        addDomainToAccountAction(selectedCustomerIds);
        handleFinish();
      }, selectedPacks.current);
    } else {
      await addPacksToGroupTemplate(groupId, [...selectedPacks.current.values()], []);
      await handleFinish();
    }
  };

  const nextConfigButton = getNextButtonText(groupId > 0, isConfigDirty);

  const infoText = `License quantities for each Pack have been calculated based on the users from the previously selected customer accounts.
    Quantities may adjust automatically as new users are added.`;

  return (
    <>
      <Dimmer active={isLoadingGroupInfo} inverted>
        <Loader active={isLoadingGroupInfo} />
      </Dimmer>
      <Wizard
        title="Create Template"
        finishButtonLabel={shouldShowConfirmationModal ? "Continue" : "Finish"}
        onCancel={onCancel}
        onProgress={onNext}
        onRegress={onPrevious}
        onFinish={handleOnFinish}
        isSaveInProgress={false}
      >
        <Section
          label="Configuration"
          className="scrollable-content"
          isLocked={!isConfigValid}
          nextButtonLabel={nextConfigButton}
          required
        >
          <CreateConfiguration
            acceptHandlers={wizardPages[Pages.Configuration]}
            onIsValidChange={setIsConfigValid}
            onIsDirtyChange={setIsConfigDirty}
            isDirty={isConfigDirty}
          />
        </Section>
        <Section label="Enrollment" className="scrollable-content" isLocked={!isEnrollmentValid}>
          <CreateEnrollment acceptHandlers={wizardPages[Pages.Enrollment]} />
        </Section>
        <Section label="Customers" className="scrollable-content">
          <CreateCustomers acceptHandlers={wizardPages[Pages.Customers]} />
        </Section>
        <Section label="Packs" className="scrollable-content">
          <CreatePacks acceptHandlers={wizardPages[Pages.Packs]} packMap={selectedPacks.current} />
        </Section>
        <Section
          label="Notify"
          className="scrollable-content"
          isLocked={!(notyStep.isNotifyStepLoaded && notyStep.isNotifyStepValid)}
          preRender
        >
          <NotifyWizardStep
            templateType={TemplateTypes.ApproveGroupTemplate}
            onLoaded={notyStep.onNotifyStepLoaded}
            onValidChange={notyStep.onNotifyTabValidChanged}
            shouldNotify={selectedCustomerIds.length > 0 && notyStep.shouldNotify}
            communicationChannel={notyStep.communicationChannel}
            hideCustomSender
            isDisabled={selectedCustomerIds.length <= 0}
            renderSwitch={(switchProps) => (
              <NotifyStepSwitch
                config={notyStep.notifyConfig}
                onNotifyConfigChange={notyStep.setNotifyConfig}
                switchProps={switchProps}
                tooltipText="Enable this option to automatically notify new customer admins when they are added to this template."
              />
            )}
          />
        </Section>
      </Wizard>
      <GroupTemplatePacksConfirmationModal
        onTriggerModalObserver={onTriggerPacksConfirmationObserver}
        packAccountItemsText={infoText}
      />
    </>
  );
}

const getNextButtonText = (isCreated: boolean, isDirty: boolean) => {
  if (!isCreated) {
    return "Save & Continue";
  }

  return isDirty ? "Update & Continue" : "Continue";
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const { groupTemplate } = state.people;

  return {
    groupId: groupTemplate.base.groupId,
    isLoadingGroupInfo: groupTemplate.base.isLoading,
    isEnrollmentValid: groupTemplate.base.isEnrollmentValid,
    selectedCustomerIds: state.people.groupTemplate.availableCustomers.customersToAdd,
    selectedPackIds: state.people.groupTemplate.availablePacks.packsToAdd,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    addDomainToAccountAction: bindAction(addDomainToAccount, dispatch),
    addPacksToGroupTemplate: bindAction(savePacks, dispatch),
    addNotificationSettings: bindAction(createNotificationSettings, dispatch),
    sendNotification: bindAction(sendTransientNotification, dispatch),
    resetTemplateState: () => dispatch(reset()),
    resetCustomers: () => dispatch(resetCustomers()),
    resetAvailableDomainsAction: bindAction(resetAvailableDomains, dispatch),
  };
};
const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(CreateGroupTemplate);
export default ConnectedComponent;
